import { useMemo, useRef, useState } from "react";

import {
  DEFAULT_INFERENCE_SETTINGS,
  TEMPLATE_PERSONAS,
  type InferenceSettings,
  type Persona,
  type ReasoningLevel,
} from "@workspace/personas";
import { FileText, Upload } from "lucide-react";

import type { Model } from "../../types";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Textarea,
} from "../ui";
import { ModelBadges } from "./model-badges";
import { VoicePicker } from "./voice-picker";

const REASONING_LEVELS: { value: ReasoningLevel; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "on", label: "On" },
];

/** Max size for an uploaded prompt file (64 KB — bounds prompt length). */
const MAX_PROMPT_FILE_BYTES = 64 * 1024;

interface PersonaBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional persona to seed the builder from (e.g. "customise a template"). */
  seed?: Persona;
  models: Model[];
  /** ws(s):// voice service URL — enables the voice picker + preview. */
  voiceApiUrl?: string;
  /** Persist the built persona (insert or update). */
  onSave: (persona: Persona) => void;
}

/** Working state for the in-progress persona (drives a future live preview). */
interface Draft {
  id: string;
  name: string;
  icon: string;
  description: string;
  modelId: string;
  settings: InferenceSettings;
  reasoning: ReasoningLevel;
  systemPrompt: string;
  /** Assigned voice id, or null to inherit the global default. */
  voice: string | null;
}

function newPersonaId(): string {
  // crypto.randomUUID is available in all target browsers; fall back to a
  // timestamp-free random string is unnecessary here.
  return `persona-${crypto.randomUUID()}`;
}

function draftFromSeed(seed: Persona | undefined, models: Model[]): Draft {
  if (seed) {
    return {
      // Cloning a template/bare model yields a NEW user persona id; editing an
      // existing user persona keeps its id (so save updates in place).
      id: seed.source === "user" ? seed.id : newPersonaId(),
      name: seed.source === "user" ? seed.name : `${seed.name} (copy)`,
      icon: seed.icon ?? "",
      description: seed.description ?? "",
      modelId: seed.modelId,
      settings: { ...seed.settings },
      reasoning: seed.reasoning,
      systemPrompt: seed.systemPrompt,
      voice: seed.voice ?? null,
    };
  }
  return {
    id: newPersonaId(),
    name: "",
    icon: "",
    description: "",
    modelId: models[0]?.id ?? "",
    settings: { ...DEFAULT_INFERENCE_SETTINGS },
    reasoning: "off",
    systemPrompt: "",
    voice: null,
  };
}

const STEPS = [
  {
    title: "Choose a model",
    help: "Smaller models are faster; larger models are more capable. Models can have different abilities like reasoning and vision, and feel different in use.",
  },
  {
    title: "Customise parameters",
    help: "Optional. Change the settings used for inference to affect the character of the output, e.g. analytical vs. creative.",
  },
  {
    title: "Custom prompt",
    help: 'Optional. Instructions given to the model before each message — e.g. "You\'re an associate in creative marketing", "Use a friendly, relatable tone", or "Use vocabulary a 10-year-old could understand".',
  },
] as const;

export function PersonaBuilder({
  open,
  onOpenChange,
  seed,
  models,
  voiceApiUrl,
  onSave,
}: PersonaBuilderProps) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(() => draftFromSeed(seed, models));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Re-seed when the dialog (re)opens with a different seed. Keyed on open+seed
  // id so reopening the builder fresh resets the wizard.
  const seedKey = `${open}:${seed?.id ?? "blank"}`;
  const lastSeedKey = useRef(seedKey);
  if (seedKey !== lastSeedKey.current) {
    lastSeedKey.current = seedKey;
    if (open) {
      setDraft(draftFromSeed(seed, models));
      setStep(0);
      setFileError(null);
    }
  }

  const setParam = (key: keyof InferenceSettings, value: number) =>
    setDraft((d) => ({ ...d, settings: { ...d.settings, [key]: value } }));

  const canSave = draft.name.trim().length > 0 && draft.modelId.length > 0;

  const handleFile = (file: File) => {
    setFileError(null);
    const isText =
      file.type === "text/plain" ||
      file.type === "text/markdown" ||
      /\.(txt|md|markdown)$/i.test(file.name);
    if (!isText) {
      setFileError("Please upload a plain text or markdown file.");
      return;
    }
    if (file.size > MAX_PROMPT_FILE_BYTES) {
      setFileError("File is too large (max 64 KB).");
      return;
    }
    file
      .text()
      .then((text) => setDraft((d) => ({ ...d, systemPrompt: text })))
      .catch(() => setFileError("Could not read the file."));
  };

  const handleSave = () => {
    if (!canSave) return;
    const persona: Persona = {
      id: draft.id,
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      icon: draft.icon.trim() || undefined,
      source: "user",
      modelId: draft.modelId,
      settings: draft.settings,
      reasoning: draft.reasoning,
      systemPrompt: draft.systemPrompt,
      voice: draft.voice ?? undefined,
      createdAt: Date.now(),
    };
    onSave(persona);
    onOpenChange(false);
  };

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  // Templates available to "start from" in step 1.
  const startFromTemplates = useMemo(() => TEMPLATE_PERSONAS, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {seed?.source === "user" ? "Edit persona" : "Create a persona"}
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEPS.length}: {current.title}
          </DialogDescription>
        </DialogHeader>

        {/* Step progress dots */}
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <span
              key={s.title}
              className={
                "h-1 flex-1 rounded-full " +
                (i <= step ? "bg-primary" : "bg-muted")
              }
            />
          ))}
        </div>

        <p className="text-xs text-muted-foreground">{current.help}</p>

        {/* Identity row — always visible so the persona has a name. */}
        {step === 0 && (
          <div className="flex gap-2">
            <Input
              aria-label="Persona icon (emoji)"
              placeholder="🙂"
              value={draft.icon}
              maxLength={2}
              className="w-14 text-center"
              onChange={(e) => setDraft((d) => ({ ...d, icon: e.target.value }))}
            />
            <Input
              aria-label="Persona name"
              placeholder="Persona name"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </div>
        )}

        {/* ── Step 1: model ──────────────────────────────────────────── */}
        {step === 0 && (
          <div className="space-y-3">
            {seed === undefined && startFromTemplates.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Or start from a template:{" "}
                <span className="inline-flex flex-wrap gap-1">
                  {startFromTemplates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="rounded border border-border px-1.5 py-0.5 hover:bg-accent"
                      onClick={() => {
                        setDraft(draftFromSeed(t, models));
                        setStep(0);
                      }}
                    >
                      {t.icon} {t.name}
                    </button>
                  ))}
                </span>
              </div>
            )}
            <div className="grid gap-2">
              {models.map((model) => {
                const selected = model.id === draft.modelId;
                return (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({ ...d, modelId: model.id }))
                    }
                    className={
                      "flex flex-col items-start gap-1 rounded-md border p-2.5 text-left transition-colors " +
                      (selected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-accent")
                    }
                  >
                    <span className="text-sm font-medium">
                      {model.id.split("/").pop()}
                    </span>
                    <ModelBadges model={model} />
                  </button>
                );
              })}
              {models.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No models available.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: parameters ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Reasoning</Label>
              <Select
                value={draft.reasoning}
                onValueChange={(v) =>
                  setDraft((d) => ({ ...d, reasoning: v as ReasoningLevel }))
                }
              >
                <SelectTrigger className="w-full h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONING_LEVELS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <VoicePicker
              voiceApiUrl={voiceApiUrl}
              value={draft.voice}
              onChange={(v) => setDraft((d) => ({ ...d, voice: v }))}
              label="Voice"
              defaultOptionLabel="Default voice (inherit)"
            />

            <ParamSlider
              label="Temperature"
              value={draft.settings.temperature}
              min={0}
              max={2}
              step={0.1}
              onChange={(v) => setParam("temperature", v)}
            />
            <ParamSlider
              label="Top P"
              value={draft.settings.topP}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => setParam("topP", v)}
            />
            <ParamSlider
              label="Top K"
              value={draft.settings.topK}
              min={1}
              max={100}
              step={1}
              onChange={(v) => setParam("topK", v)}
            />
            <ParamSlider
              label="Min P"
              value={draft.settings.minP}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => setParam("minP", v)}
            />
            <ParamSlider
              label="Presence Penalty"
              value={draft.settings.presencePenalty}
              min={-2}
              max={2}
              step={0.1}
              onChange={(v) => setParam("presencePenalty", v)}
            />
            <ParamSlider
              label="Repetition Penalty"
              value={draft.settings.repetitionPenalty}
              min={0.5}
              max={2}
              step={0.05}
              onChange={(v) => setParam("repetitionPenalty", v)}
            />
          </div>
        )}

        {/* ── Step 3: prompt ─────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-3">
            <Textarea
              value={draft.systemPrompt}
              onChange={(e) =>
                setDraft((d) => ({ ...d, systemPrompt: e.target.value }))
              }
              placeholder="You are a helpful assistant…"
              rows={8}
              className="text-sm"
            />
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.markdown,text/plain,text/markdown"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" />
                Upload .txt / .md
              </Button>
              {draft.systemPrompt && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" />
                  {draft.systemPrompt.length} chars
                </span>
              )}
            </div>
            {fileError && (
              <p className="text-xs text-destructive">{fileError}</p>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {isLast ? (
            <Button size="sm" disabled={!canSave} onClick={handleSave}>
              Save persona
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={step === 0 && !canSave}
              onClick={() => setStep(step + 1)}
            >
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParamSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <span className="font-mono text-xs text-muted-foreground">{value}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  );
}
